/**
 * Echo Hunt -- the Clichés tab: browser/editor for the cliché blacklist (P9 follow-up, §5.5 + §13.3).
 *
 * The blacklist is a list of phrases that must never appear in a bullet. Every model step reads it, so an
 * edit takes effect on the next call with no other plumbing -- and a MISTAKE takes effect just as
 * immediately, which is why this tab exists rather than append-only growth in P13:
 *  - a phrase typed by accident can be removed;
 *  - bad phrasing can be fixed (`RenameClicheEntry` keeps the entry's `source` and `hits`, so §13.3's
 *    hit counts survive a rename);
 *  - a one-word entry triggers a warning, because a single word matches any bullet containing it and each
 *    rejected bullet costs round supply (§16.2 P7);
 *  - phrases the user reported at reveals are listed as CANDIDATES with a one-click add, so §13.3's
 *    "grow the blacklist from your reveal-time flags" is usable now. Promotion stays a deliberate action:
 *    a reported tell describes what the user NOTICED, which is not necessarily a phrase a fake should be
 *    banned from using.
 *
 * ## Why a tab rather than a Settings panel
 *
 * Same reasoning as the Worlds/Characters tabs (P8): this is a list you curate occasionally and read
 * often, not a knob you turn while tuning prompts. It also has an inbox (the reported candidates), which
 * does not belong in a settings scroll.
 */
import React, {useState} from "react";
import {observer_mgl} from "mobx-graphlink";
import {Button, Column, Row, Text, TextInput} from "react-vcomponents";
import {ScrollView} from "react-vscrollview";
import {ES, InfoButton} from "web-vcore";
import {GetMyEchoProfile, RemoveClicheEntries_Live, SetClicheBlacklist_Live} from "../../../EchoHunt/Profiles_Live.js";
import {GetMyEchoRounds} from "../../../EchoHunt/Round_Live.js";
import {
	AddClicheEntry,
	CollectTellPhrases,
	IsRiskyClichePhrase,
	MIN_SAFE_CLICHE_WORDS,
	RenameClicheEntry,
} from "../../../EchoHunt/Blacklist.js";
import {ClicheBlacklistEntry} from "../../../EchoHunt/Types.js";

export const BlacklistPanel = observer_mgl(()=>{
	const profile = GetMyEchoProfile();

	return (
		<ScrollView style={ES({flex: 1, padding: 10})} contentStyle={{display: "flex", flexDirection: "column"}}>
			<Text>Echo Hunt -- Clichés</Text>
			{profile == null
				? <Text mt={10} style={{opacity: .7}}>Create an Echo Hunt profile to edit the cliché blacklist.</Text>
				: <Column mt={10} style={{gap: 16}}>
					<BlacklistEditor/>
					<TellPhraseCandidates/>
				</Column>}
		</ScrollView>
	);
});

// the list
// ==========

const BlacklistEditor = observer_mgl(()=>{
	const profile = GetMyEchoProfile();
	const [newPhrase, setNewPhrase] = useState("");
	const [error, setError] = useState<string|null>(null);
	const [busy, setBusy] = useState(false);
	const [selected, setSelected] = useState<Set<string>>(new Set());

	if (profile == null) return null;
	const entries = profile.clicheBlacklist ?? [];

	const save = async(next: ClicheBlacklistEntry[])=>{
		setBusy(true);
		setError(null);
		await SetClicheBlacklist_Live(next);
		setBusy(false);
	};

	const add = async()=>{
		const result = AddClicheEntry(entries, newPhrase);
		if (!result.ok) {
			setError(result.error);
			return;
		}
		await save(result.entries);
		setNewPhrase("");
	};

	return (
		<Column style={{gap: 4}}>
			<Row>
				<Text style={{fontWeight: "bold"}}>Cliché blacklist</Text>
				<InfoButton ml={5} text={[
					"§5.5, §13.3: phrases a bullet must never contain. Applied by the deterministic §7 check, which runs on",
					"the fake generator, the shared reshape, the corpus index pass and the woven text -- so every stage of the",
					"pipeline reads this list.",
					"",
					"Entries are a phrase, matched case-INSENSITIVELY and with flexible whitespace. That means a rename keeps",
					"working, but it also means a short entry is expensive: a single word matches any bullet containing that",
					"word, and a rejected bullet is regenerated or dropped, which shrinks the round.",
					"",
					"Phrases reported at a reveal show up in the box below, waiting to be added deliberately. P13 will make that",
					"a one-click confirm; the list itself is editable now so an accidental or badly-worded entry is never",
					"permanent.",
				].join("\n")}/>
			</Row>

			<Text style={{opacity: .8}}>{`${entries.length} phrase(s)`}</Text>

			<Row mt={4} style={{gap: 6, flexWrap: "wrap"}}>
				<TextInput width={320} value={newPhrase} placeholder="e.g. neatly resolved"
					onChange={setNewPhrase}
					onKeyDown={(e: React.KeyboardEvent)=>{
						if (e.key == "Enter") add();
					}}/>
				<Button text="Add" enabled={!busy && newPhrase.trim().length > 0} onClick={add}/>
				{selected.size > 0 && <Button text={`Remove ${selected.size} selected`} enabled={!busy} onClick={async()=>{
					setBusy(true);
					setError(null);
					await RemoveClicheEntries_Live([...selected]);
					setSelected(new Set());
					setBusy(false);
				}}/>}
			</Row>

			{error != null && <Text sel style={{color: "red"}}>{error}</Text>}

			{entries.length == 0 &&
				<Text style={{opacity: .7}}>No phrases yet. The generator has a standing set of cliché rules of its own, so a fresh list is still protected.</Text>}

			<Column style={{gap: 3}}>
				{entries.map(entry=>
					<BlacklistRow key={entry.phrase} entry={entry} busy={busy}
						entries={entries}
						selected={selected.has(entry.phrase)}
						onToggleSelected={()=>{
							const next = new Set(selected);
							if (next.has(entry.phrase)) next.delete(entry.phrase);
							else next.add(entry.phrase);
							setSelected(next);
						}}
						onSave={save}
						setError={setError}/>)}
			</Column>
		</Column>
	);
});

const BlacklistRow = observer_mgl((props: {
	entry: ClicheBlacklistEntry,
	entries: ClicheBlacklistEntry[],
	selected: boolean,
	busy: boolean,
	onToggleSelected: ()=>void,
	onSave: (next: ClicheBlacklistEntry[])=>Promise<void>,
	setError: (message: string|null)=>void,
})=>{
	const {entry, entries, selected, busy, onToggleSelected, onSave, setError} = props;
	const [editing, setEditing] = useState(false);
	const [draft, setDraft] = useState(entry.phrase);

	const risky = IsRiskyClichePhrase(entry.phrase);

	return (
		<Column style={{padding: 5, borderRadius: 4, background: "rgba(255,255,255,.04)"}}>
			<Row center style={{gap: 8, flexWrap: "wrap"}}>
				<input type="checkbox" checked={selected} onChange={onToggleSelected}/>
				{!editing && <Text style={{flex: 1}}>{entry.phrase}</Text>}
				{editing &&
					<TextInput width={320} value={draft} onChange={setDraft}/>}
				<Text style={{fontSize: 11, opacity: .55}}>{`${entry.source} · ${entry.hits} hit(s)`}</Text>
				{risky &&
					<Text style={{fontSize: 11, color: "orange"}} title={`Fewer than ${MIN_SAFE_CLICHE_WORDS} words: matches any bullet containing it`}>
						⚠ short
					</Text>}

				{!editing
					? <Button text="Edit" enabled={!busy} onClick={()=>{
						setDraft(entry.phrase);
						setEditing(true);
					}}/>
					: <>
						<Button text="Save" enabled={!busy && draft.trim().length > 0} onClick={async()=>{
							const result = RenameClicheEntry(entries, entry.phrase, draft);
							if (!result.ok) {
								setError(result.error);
								return;
							}
							setError(null);
							await onSave(result.entries);
							setEditing(false);
						}}/>
						<Button text="Cancel" onClick={()=>setEditing(false)}/>
					</>}
			</Row>
		</Column>
	);
});

// phrases reported at a reveal (§10.3 -> §13.3)
// ==========

const TellPhraseCandidates = observer_mgl(()=>{
	const profile = GetMyEchoProfile();
	const rounds = GetMyEchoRounds();
	const [error, setError] = useState<string|null>(null);
	const [busy, setBusy] = useState(false);

	if (profile == null) return null;

	const candidates = CollectTellPhrases(rounds, profile.clicheBlacklist ?? []);

	const promote = async(phrase: string)=>{
		setBusy(true);
		setError(null);
		const result = AddClicheEntry(profile.clicheBlacklist ?? [], phrase, "tellFlag");
		if (!result.ok) {
			setError(result.error);
			setBusy(false);
			return;
		}
		await SetClicheBlacklist_Live(result.entries);
		setBusy(false);
	};

	return (
		<Column style={{gap: 4}}>
			<Row>
				<Text style={{fontWeight: "bold"}}>Reported at a reveal</Text>
				<InfoButton ml={5} text={[
					"§10.3 -> §13.3: tell phrases you typed on fakes you spotted. These are CANDIDATES, not entries --",
					"a reported tell describes what you noticed, which is not always a phrase a fake should be banned",
					"from using. Promote the ones worth banning; the rest stay as notes on their rounds.",
					"",
					"Promoted entries are marked with source `tellFlag`, so a later review can tell which entries came",
					"from your own reports.",
				].join("\n")}/>
			</Row>

			{error != null && <Text sel style={{color: "red"}}>{error}</Text>}

			{candidates.length == 0 &&
				<Text style={{opacity: .7}}>
					Nothing waiting. Tell phrases are collected from the reveal's "what tipped you off?" boxes.
				</Text>}

			<Column style={{gap: 3}}>
				{candidates.map(candidate=>
					<Row key={`${candidate.roundId}/${candidate.slotId}`} center style={{gap: 8, flexWrap: "wrap"}}>
						<Text style={{flex: 1}}>{candidate.phrase}</Text>
						{IsRiskyClichePhrase(candidate.phrase) &&
							<Text style={{fontSize: 11, color: "orange"}}>⚠ short</Text>}
						<Text style={{fontSize: 11, opacity: .5}}>{new Date(candidate.reportedAt).toLocaleDateString()}</Text>
						<Button text="Add to blacklist" enabled={!busy} onClick={()=>promote(candidate.phrase)}/>
					</Row>)}
			</Column>
		</Column>
	);
});
